import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress,
  Container, FormControlLabel, IconButton, MenuItem, Paper,
  Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { selectIsPreviewActive } from "../features/rolePreview/rolePreviewSlice";
import Loading from "../components/spinners/Loading";
import {
  useDownloadEventMediaMutation,
  useGetMemberEventsQuery,
  useRegisterParticipationMutation,
  useSubmitRsvpMutation,
} from "../features/events/eventsApiSlice";
import { useGetMyRegistrationQuery } from "../features/eventRegistrations/eventRegistrationsApiSlice";
import { setDialog } from "../features/dialog/dialogSlice";
import EventBookingDialog from "../components/events/EventBookingDialog";
import {
  formatDate, formatDateRange, formatCurrency, getPaymentMethodLabel,
  toLabel, toDisplayName, getEventTypeColor, isTestingType,
  formatAddressLine, toMultilineText,
  getEventSelectableMembers, roundCurrency, getEventPricingOptions,
  getDefaultPricingOptionId, toUniqueMemberIdsPreserveOrder,
  toSortedUniqueMemberIds, calculateStandardEventPricingPreview,
  getInitialStateByEvent,
  BOOKING_STATUS_MAP,
  EventMetaRow, DetailSection, RsvpMemberCard, TestingInfoBanner,
} from "./memberEventHelpers";

// ─────────────────────────────────────────────────────────────────────────────

export default function MemberEventDetailPage() {
  // ── Hooks ──
  const { eventId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isPreviewActive = useSelector(selectIsPreviewActive);

  const {
    data: events = [],
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetMemberEventsQuery();

  const event = useMemo(
    () => (events || []).find((e) => String(e?._id) === String(eventId)) || null,
    [events, eventId],
  );

  const isBooking = event?.registrationType === "booking";

  // Booking: fetch registration (skip for non-booking; hook still called for rules-of-hooks)
  const { data: myRegData, isFetching: isFetchingMyRegistration } =
    useGetMyRegistrationQuery(eventId, { skip: !isBooking || !event });

  const [registerParticipation, { isLoading: isSavingParticipation }] =
    useRegisterParticipationMutation();
  const [submitRsvp] = useSubmitRsvpMutation();
  const [downloadEventMedia, { isLoading: isDownloading }] =
    useDownloadEventMediaMutation();

  const [bookingOpen, setBookingOpen] = useState(false);
  const [rsvpLoading, setRsvpLoading] = useState({});
  const [photoOptOutState, setPhotoOptOutState] = useState({});

  const initialStateByEvent = useMemo(
    () => (event ? getInitialStateByEvent([event]) : {}),
    [event],
  );
  const [selectionByEvent, setSelectionByEvent] = useState({});

  useEffect(() => {
    setSelectionByEvent(initialStateByEvent);
  }, [initialStateByEvent]);

  // ── Handler functions ──

  const updateEventSelection = (evtId, updater) => {
    const key = String(evtId);
    setSelectionByEvent((prev) => {
      const current = prev[key] || {
        selectedMemberIds: [],
        photoOptOutByMember: {},
        selectedPricingOptionIdByMember: {},
      };
      return { ...prev, [key]: updater(current) };
    });
  };

  const handleToggleMember = (evt, memberId, checked, locked = false) => {
    const evtId = String(evt?._id || evt?.id || "");
    if (!evtId) return;
    const memberKey = String(memberId);
    const defaultPricingOptionId = getDefaultPricingOptionId(evt);
    updateEventSelection(evtId, (current) => {
      if (!checked && locked) return current;
      const selected = new Set(current.selectedMemberIds || []);
      const nextPricingByMember = { ...(current.selectedPricingOptionIdByMember || {}) };

      if (checked) {
        selected.add(memberKey);
        if (!nextPricingByMember[memberKey] && defaultPricingOptionId) {
          nextPricingByMember[memberKey] = defaultPricingOptionId;
        }
      } else {
        selected.delete(memberKey);
        delete nextPricingByMember[memberKey];
      }

      return {
        ...current,
        selectedMemberIds: Array.from(selected),
        selectedPricingOptionIdByMember: nextPricingByMember,
      };
    });
  };

  const handleSelectAll = (evt, members = []) => {
    const evtId = String(evt?._id || evt?.id || "");
    if (!evtId) return;
    const defaultPricingOptionId = getDefaultPricingOptionId(evt);

    updateEventSelection(evtId, (current) => {
      const nextPricingByMember = { ...(current.selectedPricingOptionIdByMember || {}) };
      members.forEach((member) => {
        const memberId = String(member._id);
        if (!nextPricingByMember[memberId] && defaultPricingOptionId) {
          nextPricingByMember[memberId] = defaultPricingOptionId;
        }
      });

      return {
        ...current,
        selectedMemberIds: members.map((m) => String(m._id)),
        selectedPricingOptionIdByMember: nextPricingByMember,
      };
    });
  };

  const handleClearAll = (evt, lockedMemberIds = []) => {
    const evtId = String(evt?._id || evt?.id || "");
    if (!evtId) return;
    const lockedIds = lockedMemberIds.map((id) => String(id));

    updateEventSelection(evtId, (current) => {
      const nextPricingByMember = { ...(current.selectedPricingOptionIdByMember || {}) };
      Object.keys(nextPricingByMember).forEach((memberId) => {
        if (!lockedIds.includes(memberId)) {
          delete nextPricingByMember[memberId];
        }
      });

      return {
        ...current,
        selectedMemberIds: lockedIds,
        selectedPricingOptionIdByMember: nextPricingByMember,
      };
    });
  };

  const handlePhotoOptOutChange = (evtId, memberId, checked) => {
    updateEventSelection(evtId, (current) => ({
      ...current,
      photoOptOutByMember: { ...(current.photoOptOutByMember || {}), [String(memberId)]: checked },
    }));
  };

  const handlePricingOptionChange = (evtId, memberId, optionId) => {
    updateEventSelection(evtId, (current) => ({
      ...current,
      selectedPricingOptionIdByMember: {
        ...(current.selectedPricingOptionIdByMember || {}),
        [String(memberId)]: String(optionId || "").trim(),
      },
    }));
  };

  const handleSaveParticipation = async (evt) => {
    const eventKey = String(evt._id);
    const state = selectionByEvent[eventKey] || {
      selectedMemberIds: [],
      photoOptOutByMember: {},
      selectedPricingOptionIdByMember: {},
    };
    const selectedMemberIds = toUniqueMemberIdsPreserveOrder(state.selectedMemberIds || []);
    const pricingPreview = calculateStandardEventPricingPreview({
      event: evt,
      selectedMemberIds,
      selectedPricingOptionIdByMember: state.selectedPricingOptionIdByMember || {},
    });
    const participants = selectedMemberIds.map((memberId) => ({
      memberId,
      photoOptOut: Boolean(state.photoOptOutByMember?.[memberId]),
      selectedPricingOptionId: pricingPreview.optionIdByMember?.[memberId] || undefined,
      paidAmount:
        typeof pricingPreview.amountByMember?.[memberId] === "number"
          ? pricingPreview.amountByMember[memberId]
          : undefined,
    }));
    try {
      await registerParticipation({ id: evt._id, participants, replace: true }).unwrap();
      dispatch(
        setDialog({
          type: "SUCCESS",
          title: "Teilnahme gespeichert",
          data: {
            successText:
              participants.length > 0 ? "Anmeldung wurde gespeichert." : "Abmeldung wurde gespeichert.",
          },
        }),
      );
      refetch();
    } catch (requestError) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Fehler beim Speichern",
          data: {
            errorText:
              requestError?.data?.message || "Teilnahme konnte nicht gespeichert werden.",
          },
        }),
      );
    }
  };

  const handleRsvp = async (evt, memberId, action) => {
    const evtId = String(evt._id);
    setRsvpLoading((prev) => ({
      ...prev,
      [evtId]: { ...(prev[evtId] || {}), [memberId]: action },
    }));

    const photoOptOut =
      photoOptOutState?.[evtId]?.[memberId] ??
      (evt.contractMembersOnAttendeeList || []).find((m) => String(m._id) === memberId)
        ?.photoOptOut ??
      false;

    try {
      await submitRsvp({ id: evt._id, memberId, action, photoOptOut }).unwrap();
    } catch (requestError) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: action === "zusagen" ? "Zusage fehlgeschlagen" : "Absage fehlgeschlagen",
          data: {
            errorText: requestError?.data?.message || "Rückmeldung konnte nicht gespeichert werden.",
          },
        }),
      );
    } finally {
      setRsvpLoading((prev) => ({
        ...prev,
        [evtId]: { ...(prev[evtId] || {}), [memberId]: null },
      }));
    }
  };

  const handleRsvpPhotoOptOut = async (evt, memberId, photoOptOut) => {
    const evtId = String(evt._id);
    setPhotoOptOutState((prev) => ({
      ...prev,
      [evtId]: { ...(prev[evtId] || {}), [memberId]: photoOptOut },
    }));
    try {
      await submitRsvp({ id: evt._id, memberId, action: "zusagen", photoOptOut }).unwrap();
    } catch (requestError) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Fehler beim Aktualisieren",
          data: {
            errorText: requestError?.data?.message || "Einwilligung konnte nicht gespeichert werden.",
          },
        }),
      );
    }
  };

  const handleDownloadMedia = async (evt, media) => {
    try {
      const blob = await downloadEventMedia({ id: evt._id, mediaId: media._id }).unwrap();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = media.fileName || "event-media";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (requestError) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Download fehlgeschlagen",
          data: {
            errorText:
              requestError?.data?.message || "Datei konnte nicht heruntergeladen werden.",
          },
        }),
      );
    }
  };

  // ── Early returns ──
  if (isLoading || isFetching) return <Loading />;

  if (isPreviewActive && isError && !events.length) {
    return (
      <Container maxWidth="sm" sx={{ mt: 6 }}>
        <Helmet><title>Veranstaltung | Mitgliederverwaltung</title></Helmet>
        <Alert severity="info" variant="outlined" sx={{ py: 2 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Keine Mitgliedsdaten vorhanden
          </Typography>
          <Typography variant="body2">
            Ihr Benutzerkonto ist nicht als Mitglied verknüpft. Die Mitgliedsansicht
            kann daher keine echten Vertragsdaten laden.
          </Typography>
        </Alert>
      </Container>
    );
  }

  if (!event) {
    return (
      <Container maxWidth="sm" sx={{ pt: 3, pb: 5 }}>
        <Helmet><title>Veranstaltung nicht gefunden</title></Helmet>
        <Button
          startIcon={<Icon icon="mdi:arrow-left" width={18} />}
          onClick={() => navigate("/dashboard/veranstaltungen")}
          sx={{ mb: 2 }}
        >
          Zurück zu Veranstaltungen
        </Button>
        <Alert severity="error">
          Die Veranstaltung wurde nicht gefunden oder ist nicht mehr verfügbar.
        </Alert>
      </Container>
    );
  }

  // ── Booking event ──
  if (isBooking) {
    const myRegistration = myRegData?.registration || null;
    const paymentInfo = myRegData?.paymentInfo || null;
    const isBooked = myRegistration && myRegistration.registrationStatus !== "cancelled";
    const statusCfg = BOOKING_STATUS_MAP[myRegistration?.registrationStatus] || null;
    const addressLine = formatAddressLine(event);
    const hasMedia = (event.media || []).length > 0;

    return (
      <Container maxWidth="sm" sx={{ pt: 3, pb: 5 }}>
        <Helmet>
          <title>{event.eventName || "Veranstaltung"} | Veranstaltungen</title>
        </Helmet>

        <Button
          startIcon={<Icon icon="mdi:arrow-left" width={18} />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Zurück zu Veranstaltungen
        </Button>

        <Stack spacing={2}>
          {/* Header: chips + title */}
          <Box>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
              <Chip
                label={toLabel(event.eventType)}
                size="small"
                color={getEventTypeColor(event.eventType)}
              />
              <Chip label="Buchungs-Event" size="small" color="primary" variant="outlined" />
              {statusCfg && (
                <Chip
                  label={statusCfg.label}
                  size="small"
                  color={statusCfg.color}
                  variant="outlined"
                />
              )}
              {event.status === "abgesagt" && (
                <Chip label="Abgesagt" size="small" color="error" />
              )}
            </Stack>
            <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.3 }}>
              {event.eventName || "Veranstaltung"}
            </Typography>
          </Box>

          {/* Meta info paper */}
          <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, bgcolor: "background.default" }}>
            <Stack spacing={0.6}>
              <EventMetaRow icon="mdi:calendar-month">
                {formatDateRange(event.eventDateBegin, event.eventDateEnd)}
              </EventMetaRow>
              {(event.locationName || event.city) && (
                <EventMetaRow icon="mdi:map-marker">{event.locationName || event.city}</EventMetaRow>
              )}
              {event.registrationConfig?.registrationDeadline && (
                <EventMetaRow icon="mdi:clock-outline">
                  Anmeldeschluss: {formatDate(event.registrationConfig.registrationDeadline)}
                </EventMetaRow>
              )}
              {event.registrationConfig?.maxRegistrations > 0 && (
                <EventMetaRow icon="mdi:account-group">
                  Maximale Teilnehmerzahl: {event.registrationConfig.maxRegistrations}
                </EventMetaRow>
              )}
            </Stack>
          </Paper>

          {/* Description */}
          {event.description && (
            <DetailSection icon="mdi:text-box-outline" title="Beschreibung">
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ whiteSpace: "pre-line", lineHeight: 1.65 }}
              >
                {toMultilineText(event.description)}
              </Typography>
            </DetailSection>
          )}

          {/* Address */}
          {addressLine && (
            <DetailSection icon="mdi:map-marker-path" title="Adresse">
              <Typography variant="body2">{addressLine}</Typography>
            </DetailSection>
          )}

          {/* Booking status section */}
          <DetailSection icon="mdi:clipboard-check-outline" title="Buchung">
            <Stack spacing={1}>
              {isFetchingMyRegistration ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Buchungsstatus wird geladen...
                  </Typography>
                </Stack>
              ) : isBooked ? (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    borderColor: `${statusCfg?.color || "success"}.light`,
                  }}
                >
                  <Stack spacing={0.75}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Icon icon={statusCfg?.icon || "mdi:check-circle"} width={18} />
                      <Typography variant="body2" fontWeight={600}>
                        {statusCfg?.label}
                      </Typography>
                    </Stack>
                    {(myRegistration?.participants || []).length > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        Teilnehmer:{" "}
                        {myRegistration.participants
                          .map((p) => `${p.firstName} ${p.lastName}`)
                          .join(", ")}
                      </Typography>
                    )}
                    {myRegistration?.totalAmount > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        Betrag: {formatCurrency(myRegistration.totalAmount)}
                      </Typography>
                    )}
                    {myRegistration?.paymentMethod && (
                      <Typography variant="caption" color="text.secondary">
                        Zahlungsart: {getPaymentMethodLabel(myRegistration.paymentMethod)}
                      </Typography>
                    )}
                    {myRegistration?.registrationNumber && (
                      <Typography variant="caption" color="text.secondary">
                        Buchungsnr.: {myRegistration.registrationNumber}
                      </Typography>
                    )}
                    {paymentInfo?.reference && (
                      <Typography variant="caption" color="text.secondary">
                        Verwendungszweck: {paymentInfo.reference}
                      </Typography>
                    )}
                    {paymentInfo?.method === "bank_transfer" && (
                      <Stack spacing={0.25} sx={{ mt: 0.25 }}>
                        {paymentInfo?.recipient ? (
                          <Typography variant="caption" color="text.secondary">
                            Empfänger: {paymentInfo.recipient}
                          </Typography>
                        ) : null}
                        {paymentInfo?.iban ? (
                          <Typography variant="caption" color="text.secondary">
                            IBAN: {paymentInfo.iban}
                          </Typography>
                        ) : null}
                        {paymentInfo?.bic ? (
                          <Typography variant="caption" color="text.secondary">
                            BIC: {paymentInfo.bic}
                          </Typography>
                        ) : null}
                        <Alert severity="info" sx={{ mt: 0.5, py: 0 }}>
                          Bitte überweisen Sie den offenen Betrag mit dem angegebenen Verwendungszweck.
                        </Alert>
                      </Stack>
                    )}
                    {paymentInfo?.method === "sepa" && (
                      <Alert severity="info" sx={{ mt: 0.5, py: 0 }}>
                        Der Betrag wird vom Inhaber Ihres Vertrags per SEPA-Lastschrift eingezogen.
                      </Alert>
                    )}
                    {paymentInfo?.customText ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ whiteSpace: "pre-wrap" }}
                      >
                        {paymentInfo.customText}
                      </Typography>
                    ) : null}
                  </Stack>
                </Paper>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Für dieses Event liegt aktuell keine aktive Buchung vor.
                  </Typography>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => setBookingOpen(true)}
                    startIcon={<Icon icon="mdi:calendar-plus" width={18} />}
                  >
                    Jetzt anmelden
                  </Button>
                </>
              )}
            </Stack>
          </DetailSection>

          {/* Media */}
          {hasMedia && (
            <DetailSection icon="mdi:paperclip" title={`Anhänge (${(event.media || []).length})`}>
              <Stack spacing={0.25}>
                {(event.media || []).map((media) => (
                  <Stack
                    key={media._id}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ py: 0.5 }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Icon icon="mdi:image-outline" width={16} color="#94a3b8" />
                      <Typography
                        variant="body2"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: { xs: 170, sm: 300 },
                        }}
                      >
                        {media.fileName || "Datei"}
                      </Typography>
                    </Stack>
                    <Tooltip title="Herunterladen">
                      <IconButton
                        size="small"
                        onClick={() => handleDownloadMedia(event, media)}
                        disabled={isDownloading}
                      >
                        <Icon icon="mdi:download" width={20} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                ))}
              </Stack>
            </DetailSection>
          )}
        </Stack>

        {bookingOpen && (
          <EventBookingDialog
            open={bookingOpen}
            onClose={() => setBookingOpen(false)}
            event={event}
            onSuccess={() => setBookingOpen(false)}
          />
        )}
      </Container>
    );
  }

  // ── Standard event detail ──
  const isTesting = isTestingType(event.eventType);

  // Testing data
  const testingListMembers = Array.isArray(event.contractMembersOnAttendeeList)
    ? event.contractMembersOnAttendeeList
    : [];
  const testingZugesagtCount = testingListMembers.filter(
    (m) => m.rsvpStatus === "zugesagt",
  ).length;
  const testingAbgesagtCount = testingListMembers.filter(
    (m) => m.rsvpStatus === "abgesagt",
  ).length;
  const testingAusstehendCount = testingListMembers.filter(
    (m) => m.rsvpStatus === "ausstehend",
  ).length;
  const eventRsvpLoading = rsvpLoading[String(event._id)] || {};

  // Standard event data
  const eventState = selectionByEvent[String(event._id)] || {
    selectedMemberIds: [],
    photoOptOutByMember: {},
    selectedPricingOptionIdByMember: {},
  };
  const initialEventState = initialStateByEvent[String(event._id)] || {
    selectedMemberIds: [],
    photoOptOutByMember: {},
    selectedPricingOptionIdByMember: {},
  };
  const selfRegistrationEnabled = event.allowSelfRegistration !== false;
  const lockedMemberIds = selfRegistrationEnabled
    ? (event.myParticipations || []).map((e) => String(e.memberId))
    : [];
  const lockedSet = new Set(lockedMemberIds);
  const eventPricingOptions = getEventPricingOptions(event);
  const defaultPricingOptionId = getDefaultPricingOptionId(event);
  const hasPricingOptions = Boolean(event.isFeeBased) && eventPricingOptions.length > 0;
  const displayedFeeAmount = hasPricingOptions
    ? Math.min(...eventPricingOptions.map((option) => option.amount))
    : roundCurrency(event.feeAmount);
  const selectedMemberIds = toUniqueMemberIdsPreserveOrder(
    eventState.selectedMemberIds || [],
  );
  const selectedSet = new Set(selectedMemberIds);
  const pricingPreview = calculateStandardEventPricingPreview({
    event,
    selectedMemberIds,
    selectedPricingOptionIdByMember:
      eventState.selectedPricingOptionIdByMember || {},
  });

  const selectableMembersMap = new Map(
    getEventSelectableMembers(event).map((m) => [String(m._id), m]),
  );
  selectedMemberIds.forEach((memberId) => {
    if (selectableMembersMap.has(memberId)) return;
    const fallback = (event.contractMembers || []).find(
      (e) => String(e._id) === memberId,
    );
    if (fallback) selectableMembersMap.set(memberId, fallback);
  });
  const selectableMembers = Array.from(selectableMembersMap.values());

  const hasSelectionChanged =
    toSortedUniqueMemberIds(selectedMemberIds).join("|") !==
    toSortedUniqueMemberIds(initialEventState.selectedMemberIds || []).join("|");
  const hasPhotoChanged = toSortedUniqueMemberIds(selectedMemberIds).some(
    (memberId) =>
      Boolean(eventState.photoOptOutByMember?.[memberId]) !==
      Boolean(initialEventState.photoOptOutByMember?.[memberId]),
  );
  const hasPricingChanged = hasPricingOptions
    ? toSortedUniqueMemberIds(selectedMemberIds).some(
        (memberId) =>
          String(eventState.selectedPricingOptionIdByMember?.[memberId] || "") !==
          String(
            initialEventState.selectedPricingOptionIdByMember?.[memberId] || "",
          ),
      )
    : false;
  const hasPendingRegistration =
    hasSelectionChanged || hasPhotoChanged || hasPricingChanged;

  const hasMedia = (event.media || []).length > 0;
  const addressLine = formatAddressLine(event);

  return (
    <Container maxWidth="sm" sx={{ pt: 3, pb: 5 }}>
      <Helmet>
        <title>{event.eventName || "Veranstaltung"} | Veranstaltungen</title>
      </Helmet>

      <Button
        startIcon={<Icon icon="mdi:arrow-left" width={18} />}
        onClick={() => navigate(-1)}
        sx={{ mb: 2 }}
      >
        Zurück zu Veranstaltungen
      </Button>

      <Stack spacing={2}>
        {/* Header */}
        <Box>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            <Chip
              label={toLabel(event.eventType)}
              size="small"
              color={getEventTypeColor(event.eventType)}
            />
            {event.status === "abgesagt" && (
              <Chip label="Abgesagt" size="small" color="error" />
            )}
            {event.status === "abgeschlossen" && (
              <Chip label="Abgeschlossen" size="small" color="default" />
            )}
            {isTesting && testingZugesagtCount > 0 && (
              <Chip
                label={`${testingZugesagtCount} zugesagt`}
                size="small"
                color="success"
                variant="outlined"
              />
            )}
            {isTesting && testingAbgesagtCount > 0 && (
              <Chip
                label={`${testingAbgesagtCount} abgesagt`}
                size="small"
                color="error"
                variant="outlined"
              />
            )}
            {isTesting && testingAusstehendCount > 0 && (
              <Chip
                label={`${testingAusstehendCount} ausstehend`}
                size="small"
                color="warning"
                variant="outlined"
              />
            )}
          </Stack>
          <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.3 }}>
            {event.eventName || "Veranstaltung"}
          </Typography>
        </Box>

        {/* Meta info */}
        <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, bgcolor: "background.default" }}>
          <Stack spacing={0.6}>
            <EventMetaRow icon="mdi:calendar-month">
              {formatDateRange(event.eventDateBegin, event.eventDateEnd)}
            </EventMetaRow>
            {(event.locationName || event.city) && (
              <EventMetaRow icon="mdi:map-marker">
                {event.locationName || event.city}
              </EventMetaRow>
            )}
            {!isTesting && (
              <EventMetaRow icon="mdi:account-group">
                {event.participantCount || 0}
                {event.maxParticipants ? ` / ${event.maxParticipants}` : ""} Plätze
              </EventMetaRow>
            )}
            {event.isFeeBased && (
              <EventMetaRow icon="mdi:currency-eur" highlight>
                Ab {formatCurrency(displayedFeeAmount)} pro Mitglied
              </EventMetaRow>
            )}
          </Stack>
        </Paper>

        {/* Description */}
        {event.description && (
          <DetailSection icon="mdi:text-box-outline" title="Beschreibung">
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ whiteSpace: "pre-line", lineHeight: 1.65 }}
            >
              {toMultilineText(event.description)}
            </Typography>
          </DetailSection>
        )}

        {/* Address */}
        {addressLine && (
          <DetailSection icon="mdi:map-marker-path" title="Adresse">
            <Typography variant="body2">{addressLine}</Typography>
          </DetailSection>
        )}

        {/* Fee alert */}
        {event.isFeeBased && (
          <Alert severity="warning" sx={{ py: 0.5 }}>
            Kostenpflichtig – ab {formatCurrency(displayedFeeAmount)} pro Mitglied
          </Alert>
        )}

        {/* Testing RSVP section */}
        {isTesting && (
          <DetailSection icon="mdi:medal" title="Prüfungsteilnahme">
            <Stack spacing={1.5}>
              <TestingInfoBanner membersOnList={testingListMembers.length} />
              {testingListMembers.length > 0 && (
                <Stack spacing={1}>
                  {testingListMembers.map((member) => {
                    const memberId = String(member._id);
                    const effectiveMember = {
                      ...member,
                      photoOptOut:
                        photoOptOutState?.[String(event._id)]?.[memberId] ??
                        member.photoOptOut ??
                        false,
                    };
                    return (
                      <RsvpMemberCard
                        key={memberId}
                        member={effectiveMember}
                        eventId={String(event._id)}
                        isSaving={eventRsvpLoading[memberId] || null}
                        onRsvp={(mid, action) => handleRsvp(event, mid, action)}
                        onPhotoOptOutChange={(mid, optOut) =>
                          handleRsvpPhotoOptOut(event, mid, optOut)
                        }
                      />
                    );
                  })}
                </Stack>
              )}
              {testingListMembers.length === 0 && (
                <Alert
                  severity="info"
                  icon={<Icon icon="mdi:information-outline" />}
                >
                  Sobald dein Trainer Mitglieder zur Prüfung aufstellt, erscheinen
                  sie hier und können zu- oder absagen.
                </Alert>
              )}
            </Stack>
          </DetailSection>
        )}

        {/* Standard participation section */}
        {!isTesting && (
          <DetailSection
            icon="mdi:account-check-outline"
            title={selfRegistrationEnabled ? "Teilnahme anmelden" : "Zu- oder Absage"}
          >
            <Stack spacing={1.25}>
              {!selfRegistrationEnabled && (
                <Alert severity="info" sx={{ py: 0.5 }}>
                  Selbstanmeldung ist deaktiviert. Nur Standortleiter oder Trainer
                  können Mitglieder anmelden.
                </Alert>
              )}

              {lockedMemberIds.length > 0 && (
                <Alert severity="info" sx={{ py: 0.5 }}>
                  Bereits angemeldete Mitglieder können nur vom Admin abgemeldet
                  werden.
                </Alert>
              )}

              {selectableMembers.length === 0 ? (
                <Alert severity="warning">
                  {selfRegistrationEnabled
                    ? "Keine Vertragsmitglieder für diesen Account verfügbar."
                    : "Kein Vertragsmitglied steht auf der Teilnehmerliste."}
                </Alert>
              ) : (
                <Stack spacing={1}>
                  {selfRegistrationEnabled && (
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleSelectAll(event, selectableMembers)}
                      >
                        Alle
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleClearAll(event, lockedMemberIds)}
                      >
                        Keine
                      </Button>
                    </Stack>
                  )}

                  <Stack spacing={0.75}>
                    {selectableMembers.map((member) => {
                      const memberId = String(member._id);
                      const selected = selectedSet.has(memberId);
                      const isLocked = lockedSet.has(memberId);
                      const activePricingOptionId = String(
                        eventState.selectedPricingOptionIdByMember?.[memberId] ||
                          defaultPricingOptionId ||
                          "",
                      );
                      const activePricingOption = eventPricingOptions.find(
                        (option) => option.optionId === activePricingOptionId,
                      );

                      return (
                        <Paper
                          key={memberId}
                          variant="outlined"
                          sx={{
                            px: 1.5,
                            py: 0.75,
                            borderRadius: 2,
                            borderColor: selected ? "primary.light" : "divider",
                            bgcolor: selected ? "action.selected" : "background.paper",
                            transition: "border-color 0.15s, background-color 0.15s",
                          }}
                        >
                          <Stack spacing={0.25}>
                            <FormControlLabel
                              sx={{ m: 0 }}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={selected}
                                  disabled={isLocked}
                                  onChange={(e) =>
                                    handleToggleMember(
                                      event,
                                      memberId,
                                      e.target.checked,
                                      isLocked,
                                    )
                                  }
                                />
                              }
                              label={
                                <Stack
                                  direction="row"
                                  spacing={0.75}
                                  alignItems="center"
                                >
                                  <Typography
                                    variant="body2"
                                    fontWeight={selected ? 600 : 400}
                                  >
                                    {toDisplayName(member)}
                                  </Typography>
                                  {isLocked && (
                                    <Chip
                                      label="Angemeldet"
                                      size="small"
                                      color="success"
                                      variant="outlined"
                                    />
                                  )}
                                </Stack>
                              }
                            />
                            {selected && !isLocked && (
                              <FormControlLabel
                                sx={{ m: 0, ml: { xs: 0, sm: 3.5 } }}
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={
                                      !Boolean(
                                        eventState.photoOptOutByMember?.[memberId],
                                      )
                                    }
                                    onChange={(e) =>
                                      handlePhotoOptOutChange(
                                        event._id,
                                        memberId,
                                        !e.target.checked,
                                      )
                                    }
                                  />
                                }
                                label={
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Bildverwendung für Werbung / Social Media erlaubt
                                  </Typography>
                                }
                              />
                            )}
                            {selected && hasPricingOptions && (
                              <Stack
                                sx={{ ml: { xs: 0, sm: 3.5 }, mt: 0.5 }}
                                spacing={0.5}
                              >
                                {isLocked ? (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Preisoption:{" "}
                                    {activePricingOption
                                      ? `${activePricingOption.label} (${formatCurrency(
                                          activePricingOption.amount,
                                        )})`
                                      : "Nicht gesetzt"}
                                  </Typography>
                                ) : (
                                  <TextField
                                    size="small"
                                    select
                                    label="Preisoption"
                                    value={activePricingOptionId}
                                    onChange={(e) =>
                                      handlePricingOptionChange(
                                        event._id,
                                        memberId,
                                        e.target.value,
                                      )
                                    }
                                    fullWidth
                                    sx={{ maxWidth: { xs: "100%", sm: 320 } }}
                                  >
                                    {eventPricingOptions.map((option) => (
                                      <MenuItem
                                        key={option.optionId}
                                        value={option.optionId}
                                      >
                                        {option.label} (
                                        {formatCurrency(option.amount)})
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                )}
                                {typeof pricingPreview.amountByMember?.[memberId] ===
                                  "number" && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Voraussichtlicher Betrag:{" "}
                                    {formatCurrency(
                                      pricingPreview.amountByMember[memberId],
                                    )}
                                  </Typography>
                                )}
                              </Stack>
                            )}
                          </Stack>
                        </Paper>
                      );
                    })}
                  </Stack>

                  {event.isFeeBased && selectedMemberIds.length > 0 && (
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.25,
                        borderRadius: 1.5,
                        bgcolor: "background.default",
                      }}
                    >
                      <Stack spacing={0.4}>
                        {Boolean(event.siblingDiscount?.enabled) &&
                          selectedMemberIds.length > 1 && (
                            <Typography
                              variant="caption"
                              color="success.main"
                              fontWeight={600}
                            >
                              Geschwisterrabatt aktiv (
                              {event.siblingDiscount.type === "fixed"
                                ? `${Number(event.siblingDiscount.value || 0)} EUR`
                                : `${Number(event.siblingDiscount.value || 0)}%`}
                              ) - ab dem 2. Mitglied
                            </Typography>
                          )}
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            Geschätzter Gesamtbetrag:
                          </Typography>
                          <Typography variant="body2" fontWeight={700}>
                            {formatCurrency(pricingPreview.totalAmount)}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Paper>
                  )}

                  {hasPendingRegistration && (
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => handleSaveParticipation(event)}
                      disabled={isSavingParticipation}
                      startIcon={
                        isSavingParticipation ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <Icon icon="mdi:content-save" width={18} />
                        )
                      }
                      sx={{ mt: 0.5 }}
                    >
                      {selfRegistrationEnabled
                        ? event.isFeeBased
                          ? "Kostenpflichtig anmelden"
                          : "Auswahl speichern"
                        : selectedMemberIds.length > 0
                        ? "Zusage speichern"
                        : "Absage senden"}
                    </Button>
                  )}
                </Stack>
              )}
            </Stack>
          </DetailSection>
        )}

        {/* Media */}
        {hasMedia && (
          <DetailSection
            icon="mdi:paperclip"
            title={`Anhänge (${(event.media || []).length})`}
          >
            <Stack spacing={0.25}>
              {(event.media || []).map((media) => (
                <Stack
                  key={media._id}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ py: 0.5 }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Icon icon="mdi:image-outline" width={16} color="#94a3b8" />
                    <Typography
                      variant="body2"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: { xs: 170, sm: 300 },
                      }}
                    >
                      {media.fileName || "Datei"}
                    </Typography>
                  </Stack>
                  <Tooltip title="Herunterladen">
                    <IconButton
                      size="small"
                      onClick={() => handleDownloadMedia(event, media)}
                      disabled={isDownloading}
                    >
                      <Icon icon="mdi:download" width={20} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ))}
            </Stack>
          </DetailSection>
        )}
      </Stack>
    </Container>
  );
}
